/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IPeopleCountManager } from "./IPeopleCountManager";
import { PeopleCountAddTimeEntryParams } from "./PeopleCountAddTimeEntryParams";
import { PeopleCountAddTimeRangeEntriesParams } from "./PeopleCountAddTimeRangeEntriesParams";
import { PeopleCountManagerOptions } from "./PeopleCountManagerOptions";
import { PeopleCountResult } from "./PeopleCountResult";

/** The people count manager to compute people count for a given time range and interval (e.g., each day for a week). */
export class PeopleCountManager implements IPeopleCountManager {
    /**
     * The people count result for a given time range and interval (e.g., each day for a week).
     */
    private result: PeopleCountResult;

    /**
     * Instantiates a new people count manager.
     * @param options The options.
     */
    constructor(public readonly options: PeopleCountManagerOptions) {
        this.result = {};
        this.validateOptions(options);
    }

    /**
     * Adds a time entry to the people count for given timestamp and member id.
     * It will increment the people count by one for that timestamp if:
     * - Given member was not previously counted for that timestamp.
     * - Given shift hours value is greater than zero.
     * @param params The parameters.
     * @returns The updated people count result.
     */
    public addTimeEntry(params: PeopleCountAddTimeEntryParams): PeopleCountResult {
        const {
            limits: { endTimestampMs, startTimestampMs }
        } = this.options;
        const { memberId, shiftHours, timestampMs } = params;

        if (timestampMs >= startTimestampMs && timestampMs < endTimestampMs) {
            if (!(timestampMs in this.result)) {
                this.result[timestampMs] = {
                    count: 0,
                    membersIds: new Set()
                };
            }

            const entry = this.result[timestampMs];

            if (memberId && shiftHours > 0 && !entry.membersIds.has(memberId)) {
                entry.membersIds.add(memberId);
                entry.count += 1;
            }
        }

        return this.result;
    }

    /**
     * Adds time entries to the people count for given shift start and end timestamp and member id.
     * Starting at given 'shift.startTimestampMs' and for each 'intervalMs' it will increment the people count by one for that timestamp if:
     * - Given member was not previously counted for that timestamp.
     * It will ends once 'options.limits.endTimestampMs' or 'shift.endTimestampMs' is reached.
     * @param params The parameters.
     * @returns The updated people count result.
     */
    public addTimeRangeEntries(params: PeopleCountAddTimeRangeEntriesParams): PeopleCountResult {
        const { intervalMs, limits } = this.options;
        const { endTimestampMs, memberId, startTimestampMs } = params;
        const startMs = Math.max(limits.startTimestampMs, startTimestampMs);
        const endMs = Math.min(limits.endTimestampMs, endTimestampMs);

        for (let timestampMs = startMs; timestampMs < endMs; timestampMs += intervalMs) {
            this.addTimeEntry({
                memberId,
                shiftHours: 1,
                timestampMs
            });
        }

        return this.getResult();
    }

    /**
     * Gets the people count result.
     * @returns The people count result.
     */
    public getResult(): PeopleCountResult {
        return this.result;
    }

    /**
     * Validates the options.
     * @param options The options.
     */
    private validateOptions(options: PeopleCountManagerOptions): void {
        // TODO: Create validation exception classes for each type of exception and use it here so consumers can decide which error message to show to the user.
        if (options.intervalMs <= 0) {
            throw new Error("PeopleCountManager: 'options.intervalMs' must be a positive integer strictly greater than zero.");
        } else if (options.limits.startTimestampMs <= 0) {
            throw new Error("PeopleCountManager: 'options.limits.startTimestampMs' must be a positive integer strictly greater than zero.");
        } else if (options.limits.endTimestampMs <= 0) {
            throw new Error("PeopleCountManager: 'options.limits.endTimestampMs' must be a positive integer strictly greater than zero.");
        } else if (options.limits.startTimestampMs >= options.limits.endTimestampMs) {
            throw new Error(
                "PeopleCountManager: 'options.limits.startTimestampMs' must be strictly lower than 'options.limits.endTimestampMs'."
            );
        }
    }
}
